// JSON-LD structured data builders for LVG Imob.
// See https://schema.org — RealEstateAgent, BreadcrumbList, RealEstateListing.

export const SITE_URL = 'https://lvgimob.md';
export const ORG_ID = `${SITE_URL}/#organization`;

const CATEGORY_NAMES = {
  apartamente: 'Apartamente',
  case: 'Case',
  terenuri: 'Terenuri',
  'spatii-comerciale': 'Spații comerciale',
  garajuri: 'Garaje',
  altele: 'Proprietăți',
};

// Google Business Profile data — same entity on every page via @id.
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': ORG_ID,
    name: 'LVG Imob',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo.jpg`,
    image: `${SITE_URL}/logo.jpg`,
    telephone: '+37369003757',
    email: 'lvgimob1@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Strada Ion Creangă 4',
      addressLocality: 'Ungheni',
      addressCountry: 'MD',
    },
    areaServed: [
      { '@type': 'City', name: 'Ungheni' },
      { '@type': 'City', name: 'Chișinău' },
      { '@type': 'City', name: 'Bălți' },
    ],
    sameAs: [
      'https://www.facebook.com/nicnicolas.nicnicolas/',
      'https://www.instagram.com/lvgimob/',
    ],
  };
}

// items: [{ name, item? }] — last item is the current page (no `item`).
export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: entry.name,
      ...(entry.item ? { item: entry.item } : {}),
    })),
  };
}

// Schema.org property type per category (only Apartment/House have dedicated types).
const PROPERTY_SCHEMA_TYPES = {
  apartamente: 'Apartment',
  case: 'House',
};

// listing = detail-row serialized object (e.g. LandSerializer output):
// { listing: {...}, land_surface / surface / rooms ..., surface_type: {...} }
export function realEstateListingSchema(base, listing, saleTypeId) {
  const l = listing.listing || {};
  const locSlug = l.location?.slug ?? l.location?.id;
  const canonicalUrl = `${SITE_URL}/${base}/${locSlug}/${l.slug}`;
  const aboutType = PROPERTY_SCHEMA_TYPES[base] || 'Place';

  const floorSize = {};
  const sizeValue = listing.surface ?? listing.land_surface;
  if (sizeValue != null && base !== 'terenuri') {
    floorSize.floorSize = {
      '@type': 'QuantitativeValue',
      value: sizeValue,
      unitCode: 'MTK',
    };
  }
  const rooms = listing.rooms != null ? { numberOfRooms: listing.rooms } : {};

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    '@id': `${canonicalUrl}#listing`,
    url: canonicalUrl,
    name: l.seo_title || l.street,
    description: (l.description || '').replace(/<[^>]*>/g, ' ').trim().slice(0, 300),
    datePosted: (l.created_at || '').slice(0, 10),
    about: {
      '@type': aboutType,
      name: l.seo_title || l.street,
      ...rooms,
      ...floorSize,
      address: {
        '@type': 'PostalAddress',
        addressLocality: l.location?.location || '',
        addressCountry: 'MD',
      },
    },
    offers: {
      '@type': 'Offer',
      price: String(l.price ?? ''),
      priceCurrency: 'EUR',
      url: canonicalUrl,
      seller: { '@id': ORG_ID },
    },
  };
}

// ItemList over the listings shown on a category page (position-based).
export function itemListSchema(base, results) {
  if (!results || !results.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: results.map((item, idx) => {
      const l = item.listing || {};
      const locSlug = l.location?.slug ?? l.location?.id;
      return {
        '@type': 'ListItem',
        position: idx + 1,
        name: l.seo_title || l.street,
        url: l.slug ? `${SITE_URL}/${base}/${locSlug}/${l.slug}` : `${SITE_URL}/${base}/${item.id}`,
      };
    }),
  };
}

export function categoryName(base) {
  return CATEGORY_NAMES[base] || '';
}
